const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

const toPositiveInteger = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : Number.NaN;
};

const toFinitePoint = (point) => {
  const x = toFiniteNumber(point?.x);
  const y = toFiniteNumber(point?.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const buildTicks = (min, max, count = 6) => {
  if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isInteger(count) || count < 2) return [];
  const span = max - min;
  if (!(span > 0)) return [min];
  const step = span / (count - 1);
  return Array.from({ length: count }, (_, idx) => min + idx * step);
};

const expandRange = (min, max, ratio, minSpan) => {
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return [-minSpan / 2, minSpan / 2];
  }
  let lo = Math.min(min, max);
  let hi = Math.max(min, max);
  let span = hi - lo;
  if (!(span > 0)) {
    lo -= minSpan / 2;
    hi += minSpan / 2;
    span = hi - lo;
  }
  const pad = Math.max(span * ratio, minSpan * 0.15);
  return [lo - pad, hi + pad];
};

const formatNum = (value) => {
  if (!Number.isFinite(value)) return '-';
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
};

const formatFs = (value) => {
  if (!Number.isFinite(value)) return '-';
  return (Math.round(value * 1000) / 1000).toFixed(3);
};

const buildCellBounds = (value, min, max, count) => {
  if (!Number.isFinite(value) || !Number.isFinite(min) || !Number.isFinite(max) || !(max > min)) return null;
  if (!Number.isInteger(count) || count < 2) return { min, max };
  const step = (max - min) / (count - 1);
  const half = step / 2;
  return {
    min: Math.max(min, value - half),
    max: Math.min(max, value + half),
  };
};

const getQuantile = (sortedValues, q) => {
  if (!Array.isArray(sortedValues) || sortedValues.length === 0) return Number.NaN;
  const clampedQ = Math.min(1, Math.max(0, q));
  const index = (sortedValues.length - 1) * clampedQ;
  const lo = Math.floor(index);
  const hi = Math.ceil(index);
  if (lo === hi) return sortedValues[lo];
  const weight = index - lo;
  return sortedValues[lo] + (sortedValues[hi] - sortedValues[lo]) * weight;
};

const resolveHeatmapCap = (values, minFs, maxFs) => {
  if (!Array.isArray(values) || values.length === 0) return Number.NaN;
  if (!Number.isFinite(minFs) || !Number.isFinite(maxFs) || !(maxFs > minFs)) return maxFs;

  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length < 5) return maxFs;

  // Keep cap dynamic but intentionally lower than the previous q90 strategy
  // so the heatmap shows more color variation in typical search ranges.
  const q80 = getQuantile(sorted, 0.8);
  if (!Number.isFinite(q80)) return maxFs;

  const range = maxFs - minFs;
  const lowerBound = minFs + range * 0.22;
  const upperBound = minFs + range * 0.82;
  const capped = clamp(q80, lowerBound, upperBound);
  return Math.max(minFs + 1e-6, Math.min(maxFs, capped));
};

const getCircleY = (circle, x, branch = 'lower') => {
  if (!circle || !Number.isFinite(x)) return Number.NaN;
  const dx = x - circle.centerX;
  const radicand = circle.radius * circle.radius - dx * dx;
  if (radicand < 0) return Number.NaN;
  const root = Math.sqrt(Math.max(0, radicand));
  return branch === 'upper' ? circle.centerY + root : circle.centerY - root;
};

const interpolateProfileY = (profile, x) => {
  if (!Array.isArray(profile) || profile.length < 2 || !Number.isFinite(x)) return Number.NaN;
  const eps = 1e-9;

  for (let idx = 0; idx < profile.length - 1; idx += 1) {
    const start = profile[idx];
    const end = profile[idx + 1];
    if (!start || !end) continue;

    const minX = Math.min(start.x, end.x) - eps;
    const maxX = Math.max(start.x, end.x) + eps;
    if (x < minX || x > maxX) continue;

    const dx = end.x - start.x;
    if (Math.abs(dx) < eps) return (start.y + end.y) / 2;

    const t = (x - start.x) / dx;
    return start.y + (end.y - start.y) * t;
  }

  return Number.NaN;
};

const resolveArcBranch = (profile, circle, xLeft, xRight) => {
  const xMid = (xLeft + xRight) / 2;
  const groundY = interpolateProfileY(profile, xMid);
  const yLower = getCircleY(circle, xMid, 'lower');
  const yUpper = getCircleY(circle, xMid, 'upper');

  if (Number.isFinite(groundY)) {
    const lowerUnderground = Number.isFinite(yLower) && yLower <= groundY + 1e-6;
    const upperUnderground = Number.isFinite(yUpper) && yUpper <= groundY + 1e-6;

    if (lowerUnderground && !upperUnderground) return 'lower';
    if (upperUnderground && !lowerUnderground) return 'upper';
    if (lowerUnderground && upperUnderground) {
      return Math.abs(groundY - yLower) >= Math.abs(groundY - yUpper) ? 'lower' : 'upper';
    }
  }

  return 'lower';
};

const buildCircleArcPoints = (circle, xLeft, xRight, branch, sampleCount = 96) => {
  if (!circle || !Number.isFinite(xLeft) || !Number.isFinite(xRight) || !(xRight > xLeft)) return [];
  const samples = Number.isInteger(sampleCount) && sampleCount > 1 ? sampleCount : 96;

  return Array.from({ length: samples + 1 }, (_, index) => {
    const ratio = index / samples;
    const x = xLeft + (xRight - xLeft) * ratio;
    const y = getCircleY(circle, x, branch);
    return { x, y };
  }).filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
};

const normalizeCircle = (value) => {
  const centerX = toFiniteNumber(value?.centerX);
  const centerY = toFiniteNumber(value?.centerY);
  const radius = toFiniteNumber(value?.radius);
  if (!Number.isFinite(centerX) || !Number.isFinite(centerY) || !Number.isFinite(radius) || radius <= 0) return null;
  return { centerX, centerY, radius };
};

const normalizeInterval = (value) => {
  const xLeft = toFiniteNumber(value?.xLeft);
  const xRight = toFiniteNumber(value?.xRight);
  if (!Number.isFinite(xLeft) || !Number.isFinite(xRight) || !(xRight > xLeft)) return null;
  return { xLeft, xRight };
};

const normalizeSurface = (value, fallbackFactorOfSafety = Number.NaN) => {
  const circle = normalizeCircle(value?.circle || value);
  if (!circle) return null;
  const interval = normalizeInterval(value?.interval);
  return {
    circle,
    interval,
    factorOfSafety: toFiniteNumber(value?.factorOfSafety ?? fallbackFactorOfSafety),
  };
};

const buildCircleArcFromInterval = (profile, circle, interval, sampleCount = 120) => {
  if (!circle || !interval) return null;
  const circleXMin = circle.centerX - circle.radius;
  const circleXMax = circle.centerX + circle.radius;
  const xLeft = clamp(interval.xLeft, circleXMin, circleXMax);
  const xRight = clamp(interval.xRight, circleXMin, circleXMax);
  if (!(xRight > xLeft)) return null;

  const branch = resolveArcBranch(profile, circle, xLeft, xRight);
  const points = buildCircleArcPoints(circle, xLeft, xRight, branch, sampleCount);
  if (points.length < 2) return null;
  return { xLeft, xRight, branch, points };
};

const buildRenderedSurface = ({
  profile,
  circle,
  interval,
  factorOfSafety,
  mapPoint,
  plot,
  scale,
  allowFullCircleFallback = true,
}) => {
  if (!circle || typeof mapPoint !== 'function' || !plot || !Number.isFinite(scale)) return null;

  const center = mapPoint(circle.centerX, circle.centerY);
  const radius = circle.radius * scale;
  const arc = buildCircleArcFromInterval(profile, circle, interval, 120);
  let leftIntersection = null;
  let rightIntersection = null;
  let arcPath = '';
  let radiusLines = [];

  if (arc) {
    const arcPointsPx = arc.points.map((point) => mapPoint(point.x, point.y));
    if (arcPointsPx.length >= 2) {
      const [firstPoint, ...restPoints] = arcPointsPx;
      leftIntersection = firstPoint;
      rightIntersection = arcPointsPx[arcPointsPx.length - 1];
      arcPath = `M ${firstPoint.x} ${firstPoint.y}${restPoints.map((point) => ` L ${point.x} ${point.y}`).join('')}`;
      radiusLines = [
        { x1: center.x, y1: center.y, x2: leftIntersection.x, y2: leftIntersection.y },
        { x1: center.x, y1: center.y, x2: rightIntersection.x, y2: rightIntersection.y },
      ];
    }
  }

  const hasArc = arcPath.length > 0;
  if (!hasArc && !allowFullCircleFallback) return null;

  const rawFsX = hasArc ? center.x + 10 : center.x + radius + 8;
  const rawFsY = hasArc ? center.y - 10 : center.y - radius - 8;
  const fsAnchor = rawFsX > plot.right - 8 ? 'end' : 'start';

  return {
    cx: center.x,
    cy: center.y,
    radius,
    showFullCircle: !hasArc,
    arcPath,
    leftIntersection,
    rightIntersection,
    radiusLines,
    fsLabel: `FS ${formatFs(factorOfSafety)}`,
    fsX: fsAnchor === 'end' ? plot.right - 8 : Math.max(plot.left + 8, rawFsX),
    fsY: Math.max(plot.top + 12, Math.min(plot.bottom - 6, rawFsY)),
    fsAnchor,
  };
};

export default function buildSlopeStabilitySketchModel({
  input = {},
  mode = 'single',
  criticalSurface = null,
  searchHeatmap = null,
} = {}) {
  const frame = {
    viewWidth: 920,
    viewHeight: 460,
    margin: { top: 12, right: 12, bottom: 30, left: 40 },
  };
  const plot = {
    left: frame.margin.left,
    top: frame.margin.top,
    right: frame.viewWidth - frame.margin.right,
    bottom: frame.viewHeight - frame.margin.bottom,
  };
  plot.width = plot.right - plot.left;
  plot.height = plot.bottom - plot.top;

  const warnings = [];
  const profileRaw = Array.isArray(input?.groundProfile) ? input.groundProfile : [];
  const profile = profileRaw.map(toFinitePoint).filter(Boolean);
  if (profile.length < 2) warnings.push('Enter at least two valid ground profile points to render the slope.');

  const extentsX = [];
  const extentsY = [];

  profile.forEach((point) => {
    extentsX.push(point.x);
    extentsY.push(point.y);
  });

  const waterElevation = toFiniteNumber(input?.waterTable?.elevation);
  if (Number.isFinite(waterElevation)) extentsY.push(waterElevation);

  // Slide-like slope limits for visualization:
  // keep legacy fallback so previously saved inputs still render guide lines.
  const leftLimitXRaw = toFiniteNumber(input?.surfaceFilters?.leftLimitX ?? input?.surfaceFilters?.entryX?.min);
  const rightLimitXRaw = toFiniteNumber(input?.surfaceFilters?.rightLimitX ?? input?.surfaceFilters?.exitX?.max);
  if (Number.isFinite(leftLimitXRaw)) extentsX.push(leftLimitXRaw);
  if (Number.isFinite(rightLimitXRaw)) extentsX.push(rightLimitXRaw);

  let single = null;
  if (mode === 'single') {
    const centerX = toFiniteNumber(input?.singleSurface?.centerX);
    const centerY = toFiniteNumber(input?.singleSurface?.centerY);
    const radius = toFiniteNumber(input?.singleSurface?.radius);
    if (Number.isFinite(centerX) && Number.isFinite(centerY) && Number.isFinite(radius) && radius > 0) {
      single = { centerX, centerY, radius };
      extentsX.push(centerX - radius, centerX + radius);
      extentsY.push(centerY - radius, centerY + radius);
    }
  }

  let search = null;
  if (mode === 'search') {
    const centerXMin = toFiniteNumber(input?.search?.centerX?.min);
    const centerXMax = toFiniteNumber(input?.search?.centerX?.max);
    const centerXCount = toPositiveInteger(input?.search?.centerX?.count);
    const centerYMin = toFiniteNumber(input?.search?.centerY?.min);
    const centerYMax = toFiniteNumber(input?.search?.centerY?.max);
    const centerYCount = toPositiveInteger(input?.search?.centerY?.count);
    const hasCenterRange =
      Number.isFinite(centerXMin) &&
      Number.isFinite(centerXMax) &&
      centerXMax > centerXMin &&
      Number.isFinite(centerYMin) &&
      Number.isFinite(centerYMax) &&
      centerYMax > centerYMin;

    if (hasCenterRange) {
      search = {
        centerXMin,
        centerXMax,
        centerXCount,
        centerYMin,
        centerYMax,
        centerYCount,
      };
      extentsX.push(centerXMin, centerXMax);
      extentsY.push(centerYMin, centerYMax);
    }
  }

  const criticalSurfaceNormalized = normalizeSurface(criticalSurface);
  const criticalCircle = criticalSurfaceNormalized?.circle || null;
  const criticalInterval = criticalSurfaceNormalized?.interval || null;
  const criticalArc = criticalCircle ? buildCircleArcFromInterval(profile, criticalCircle, criticalInterval, 120) : null;

  if (criticalCircle) {
    if (criticalArc) {
      criticalArc.points.forEach((point) => {
        extentsX.push(point.x);
        extentsY.push(point.y);
      });
      extentsX.push(criticalCircle.centerX);
      extentsY.push(criticalCircle.centerY);
    } else {
      extentsX.push(criticalCircle.centerX - criticalCircle.radius, criticalCircle.centerX + criticalCircle.radius);
      extentsY.push(criticalCircle.centerY - criticalCircle.radius, criticalCircle.centerY + criticalCircle.radius);
    }
  }

  if (extentsX.length === 0 || extentsY.length === 0) {
    extentsX.push(0, 45);
    extentsY.push(-2, 18);
  }

  const rawXMin = Math.min(...extentsX);
  const rawXMax = Math.max(...extentsX);
  const rawYMin = Math.min(...extentsY);
  const rawYMax = Math.max(...extentsY);
  const [xMin, xMax] = expandRange(rawXMin, rawXMax, 0.08, 10);
  const [yMin, yMax] = expandRange(rawYMin, rawYMax, 0.1, 8);

  const world = { xMin, xMax, yMin, yMax };
  const spanX = world.xMax - world.xMin;
  const spanY = world.yMax - world.yMin;
  const scale = Math.min(plot.width / Math.max(1e-6, spanX), plot.height / Math.max(1e-6, spanY));
  const drawWidth = spanX * scale;
  const drawHeight = spanY * scale;
  const offsetX = plot.left + (plot.width - drawWidth) / 2;
  const offsetY = plot.top + (plot.height - drawHeight) / 2;

  const mapPoint = (x, y) => ({
    x: offsetX + (x - world.xMin) * scale,
    y: offsetY + (world.yMax - y) * scale,
  });

  const profilePx = profile.map((point) => mapPoint(point.x, point.y));
  const profilePolyline = profilePx.map((point) => `${point.x},${point.y}`).join(' ');
  const soilPolygon =
    profilePx.length > 1
      ? [
          `${profilePx[0].x},${plot.bottom}`,
          ...profilePx.map((point) => `${point.x},${point.y}`),
          `${profilePx[profilePx.length - 1].x},${plot.bottom}`,
        ].join(' ')
      : '';

  const waterY = Number.isFinite(waterElevation) ? mapPoint(world.xMin, waterElevation).y : null;
  const waterVisible = Number.isFinite(waterY) && waterY >= plot.top - 1 && waterY <= plot.bottom + 1;

  const singlePx =
    single !== null
      ? {
          cx: mapPoint(single.centerX, single.centerY).x,
          cy: mapPoint(single.centerX, single.centerY).y,
          radius: single.radius * scale,
          centerLabel: `C(${formatNum(single.centerX)}, ${formatNum(single.centerY)})`,
          radiusLabel: `R=${formatNum(single.radius)}`,
        }
      : null;

  const searchPx =
    search !== null
      ? {
          left: mapPoint(search.centerXMin, search.centerYMin).x,
          right: mapPoint(search.centerXMax, search.centerYMin).x,
          top: mapPoint(search.centerXMin, search.centerYMax).y,
          bottom: mapPoint(search.centerXMin, search.centerYMin).y,
          centerX: mapPoint((search.centerXMin + search.centerXMax) / 2, (search.centerYMin + search.centerYMax) / 2).x,
          centerY: mapPoint((search.centerXMin + search.centerXMax) / 2, (search.centerYMin + search.centerYMax) / 2).y,
          centerXTicks: buildTicks(search.centerXMin, search.centerXMax, search.centerXCount).map(
            (value) => mapPoint(value, search.centerYMin).x
          ),
          centerYTicks: buildTicks(search.centerYMin, search.centerYMax, search.centerYCount).map(
            (value) => mapPoint(search.centerXMin, value).y
          ),
          label: `centerX ${formatNum(search.centerXMin)}..${formatNum(search.centerXMax)}, centerY ${formatNum(search.centerYMin)}..${formatNum(search.centerYMax)}`,
        }
      : null;

  const heatmapPoints =
    mode === 'search' && search !== null && Array.isArray(searchHeatmap?.points)
      ? searchHeatmap.points
          .map((point) => {
            const fs = toFiniteNumber(point?.minFs ?? point?.factorOfSafety);
            return {
              centerX: toFiniteNumber(point?.centerX),
              centerY: toFiniteNumber(point?.centerY),
              fs,
              surface: normalizeSurface(point?.surface, fs),
            };
          })
          .filter(
            (point) =>
              Number.isFinite(point.centerX) &&
              Number.isFinite(point.centerY) &&
              Number.isFinite(point.fs) &&
              point.centerX >= search.centerXMin &&
              point.centerX <= search.centerXMax &&
              point.centerY >= search.centerYMin &&
              point.centerY <= search.centerYMax
          )
      : [];

  const heatmapCells =
    mode === 'search' && search !== null && searchPx !== null && heatmapPoints.length > 0
      ? heatmapPoints
          .map((point) => {
            const xBounds = buildCellBounds(point.centerX, search.centerXMin, search.centerXMax, search.centerXCount);
            const yBounds = buildCellBounds(point.centerY, search.centerYMin, search.centerYMax, search.centerYCount);
            if (!xBounds || !yBounds) return null;

            const left = mapPoint(xBounds.min, point.centerY).x;
            const right = mapPoint(xBounds.max, point.centerY).x;
            const top = mapPoint(point.centerX, yBounds.max).y;
            const bottom = mapPoint(point.centerX, yBounds.min).y;

            const x = Math.max(searchPx.left, Math.min(left, right));
            const y = Math.max(searchPx.top, Math.min(top, bottom));
            const x2 = Math.min(searchPx.right, Math.max(left, right));
            const y2 = Math.min(searchPx.bottom, Math.max(top, bottom));
            const width = x2 - x;
            const height = y2 - y;
            if (!(width > 0 && height > 0)) return null;
            const hoverSurface = point.surface
              ? buildRenderedSurface({
                  profile,
                  circle: point.surface.circle,
                  interval: point.surface.interval,
                  factorOfSafety: point.surface.factorOfSafety,
                  mapPoint,
                  plot,
                  scale,
                  allowFullCircleFallback: false,
                })
              : null;

            return {
              x,
              y,
              width,
              height,
              fs: point.fs,
              surface: hoverSurface,
            };
          })
          .filter(Boolean)
      : [];

  const payloadMinFs = toFiniteNumber(searchHeatmap?.minFs);
  const payloadMaxFs = toFiniteNumber(searchHeatmap?.maxFs);
  const computedMinFs = heatmapCells.length > 0 ? Math.min(...heatmapCells.map((cell) => cell.fs)) : Number.NaN;
  const computedMaxFs = heatmapCells.length > 0 ? Math.max(...heatmapCells.map((cell) => cell.fs)) : Number.NaN;
  const heatmapMinFs = Number.isFinite(payloadMinFs) ? payloadMinFs : computedMinFs;
  const heatmapMaxFs = Number.isFinite(payloadMaxFs) ? payloadMaxFs : computedMaxFs;
  const heatmapCapFs = resolveHeatmapCap(
    heatmapCells.map((cell) => cell.fs),
    heatmapMinFs,
    heatmapMaxFs
  );
  const heatmapIsCapped = Number.isFinite(heatmapCapFs) && Number.isFinite(heatmapMaxFs) && heatmapCapFs < heatmapMaxFs;

  const criticalPx =
    criticalCircle !== null
      ? buildRenderedSurface({
          profile,
          circle: criticalCircle,
          interval: criticalInterval,
          factorOfSafety: criticalSurfaceNormalized?.factorOfSafety,
          mapPoint,
          plot,
          scale,
          allowFullCircleFallback: true,
        })
      : null;

  const xTicks = buildTicks(world.xMin, world.xMax, 6).map((tick) => ({
    value: tick,
    x: mapPoint(tick, world.yMin).x,
  }));
  const yTicks = buildTicks(world.yMin, world.yMax, 6).map((tick) => ({
    value: tick,
    y: mapPoint(world.xMin, tick).y,
  }));

  const xAxisY = world.yMin <= 0 && world.yMax >= 0 ? mapPoint(world.xMin, 0).y : null;
  const yAxisX = world.xMin <= 0 && world.xMax >= 0 ? mapPoint(0, world.yMin).x : null;

  const slopeLimitLeft = Number.isFinite(leftLimitXRaw)
    ? {
        value: leftLimitXRaw,
        x: mapPoint(leftLimitXRaw, world.yMin).x,
      }
    : null;
  const slopeLimitRight = Number.isFinite(rightLimitXRaw)
    ? {
        value: rightLimitXRaw,
        x: mapPoint(rightLimitXRaw, world.yMin).x,
      }
    : null;

  return {
    frame,
    plot,
    world,
    warnings,
    hasRenderableProfile: profilePx.length > 1,
    profilePx,
    profilePolyline,
    soilPolygon,
    water: {
      elevation: waterElevation,
      y: waterY,
      visible: waterVisible,
    },
    critical: criticalPx,
    heatmap: {
      enabled:
        heatmapCells.length > 0 &&
        Number.isFinite(heatmapMinFs) &&
        Number.isFinite(heatmapMaxFs) &&
        Number.isFinite(heatmapCapFs),
      cells: heatmapCells,
      minFs: heatmapMinFs,
      maxFs: heatmapMaxFs,
      capFs: heatmapCapFs,
      isCapped: heatmapIsCapped,
    },
    single: singlePx,
    search: searchPx,
    slopeLimits: {
      left: slopeLimitLeft,
      right: slopeLimitRight,
      visible: Boolean(slopeLimitLeft || slopeLimitRight),
    },
    ticks: { x: xTicks, y: yTicks },
    axes: { xAxisY, yAxisX },
    labels: {
      mode: mode === 'single' ? 'Single Surface Mode' : 'Grid Search Mode',
      xMin: formatNum(world.xMin),
      xMax: formatNum(world.xMax),
      yMin: formatNum(world.yMin),
      yMax: formatNum(world.yMax),
    },
  };
}
